import React from "react";
import { motion } from "framer-motion";
import { Scale } from "lucide-react";
import { roadmapMilestones } from "../../constants/siteContent";
import { fadeInUp, staggerContainer } from "../../lib/animations";

export const RoadmapSection = () => {
  return (
    <section id="roadmap" data-testid="roadmap-section" className="py-24 md:py-28 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="text-center mb-16 md:mb-[4.5rem]">
          <motion.p variants={fadeInUp} className="text-[#D1111A] font-semibold tracking-[0.2em] uppercase mb-3">Our Journey</motion.p>
          <motion.h2 variants={fadeInUp} className="font-editorial text-4xl md:text-6xl text-[#0A0A0A] italic">Strategic Roadmap</motion.h2>
        </motion.div>

        <div className="relative">
          <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-[#E4E4E7] md:hidden" />
          <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-0.5 bg-[#E4E4E7] -translate-x-1/2" />
          <div className="space-y-12 md:space-y-14">
            {roadmapMilestones.map((milestone, index) => (
              <motion.div
                key={`${milestone.year}-${milestone.title}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`relative flex w-full flex-col items-start gap-8 pl-12 md:pl-0 md:items-center ${index % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"}`}
              >
                <div className="absolute left-4 top-6 flex -translate-x-1/2 items-center justify-center md:hidden" data-testid={`roadmap-mobile-dot-${milestone.year}`}>
                  <span className="h-4 w-4 rounded-full border-4 border-white bg-[#D1111A] shadow-[0_0_0_2px_rgba(209,17,26,0.18)]" />
                </div>

                <div className={`w-full flex-1 ${index % 2 === 0 ? "md:text-right" : "md:text-left"}`}>
                  <div className={`inline-block w-full max-w-xl p-6 md:p-7 bg-[#F4F4F5] hover:bg-[#D1111A] hover:text-white transition-colors group ${index % 2 === 0 ? "md:ml-auto" : ""}`}>
                    <p className="font-editorial text-4xl text-[#D1111A] group-hover:text-white mb-2">{milestone.year}</p>
                    <h3 className="font-semibold text-lg mb-3">{milestone.title}</h3>
                    <ul className="space-y-2">
                      {milestone.items.map((item, itemIndex) => (
                        <li key={`${milestone.year}-${itemIndex}`} className="flex items-start gap-2 text-sm text-[#0A0A0A]/70 group-hover:text-white/80">
                          <span className="mt-1.5 h-2.5 w-2.5 rounded-full bg-[#D1111A] group-hover:bg-white flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>

                    {milestone.legalReference && (
                      <div className="mt-5 border-t border-[#0A0A0A]/10 pt-4 group-hover:border-white/20" data-testid={`roadmap-legal-reference-${milestone.year}`}>
                        <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#D1111A] group-hover:text-white/85">
                          <Scale size={14} />
                          Legal case law
                        </div>
                        <a
                          href={milestone.legalReference.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block text-sm font-semibold underline underline-offset-4 text-[#0A0A0A] group-hover:text-white"
                          data-testid={`roadmap-legal-link-${milestone.year}`}
                        >
                          {milestone.legalReference.shortTitle}
                        </a>
                        <p className="mt-1 text-[11px] text-[#0A0A0A]/55 group-hover:text-white/70">{milestone.legalReference.meta}</p>
                        <p className="mt-2 text-sm text-[#0A0A0A]/70 group-hover:text-white/80">{milestone.legalReference.summary}</p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="hidden md:flex items-center justify-center w-8">
                  <div className="w-4 h-4 rounded-full bg-[#D1111A] border-4 border-white shadow-lg" />
                </div>
                <div className="flex-1 hidden md:block" />
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};